// @flow
import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';

import { ARTICLE_PER_PAGE } from 'constants/articleConstants';
import { STATUS_SUCCESS, STATUS_FAILURE } from 'constants/statusConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import { getPublicAssets } from 'utils/assetsUtils';
import { formatFullDate, getDateFromString, formatMinutes } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';
import type { ArticleType, CategoryArticleType, CategoryType } from 'types/Article';
import type { Status } from 'types/Status';
import type { State as SidebarType } from 'reducers/sidebarReducer';
import type { FullUserType } from 'types/User';
import { nullLoadingArticle } from 'types/Article';
import Breadcrumb from 'components/fragments/Breadcrumb';
import NewsListElement from 'components/newsletter/NewsListElement';
import Pagination from 'components/newsletter/Pagination';
import SideBar from 'components/sidebar/Sidebar';
import Loading from 'components/fragments/Loading';
import BackgroundImage from 'components/fragments/BackgroundImage';
import MenuElement from 'components/header/MenuElement';
import DropdownMenuElementPlus from 'components/billboard/DropdownMenuElementPlus';
import Label from 'components/fragments/text/Label';

export type OwnProps = {
  match: {
    params: {
      category: string
    }
  }
};

type HistoryProps = {
  history: any
};

export type StateProps = {
  articles: Array<ArticleType>,
  articleNumber: number,
  categories: Array<CategoryType>,
  status: Status,
  sidebar: SidebarType,
  categoriesArticle: Array<CategoryArticleType>,
  ligue: string,
  userPref: FullUserType
};

export type DispatchProps = {
  fetchArticlesCategory: (page: number, per_page: number, category: string) => void,
  fetchGenericSidebar: () => void,
  fetchArticlesStaticCategories: () => void
};

type Props = StateProps & OwnProps & DispatchProps & HistoryProps;

type State = {
  currentPage: number,
  menuItems: Array<{ id: number, size: number }>,
  isMobile: boolean,
  hiddenMenuItems: Array<number>,
  menuElements: Array<MenuElement>,
  menuLinkX: number,
  labelPlus: string,
  reloadMenu: boolean,
  forcecategoriesArticleUpdate: boolean
};

class NewsletterCategory extends PureComponent<Props, State> {
  _menuElements: Array<?MenuElement>;
  _menuLink: ?HTMLElement;
  props: Props;
  state: State = {
    currentPage: 1,
    menuItems: [],
    isMobile: false,
    hiddenMenuItems: [],
    menuElements: [],
    menuLinkX: 0,
    labelPlus: 'Plus',
    reloadMenu: false,
    forcecategoriesArticleUpdate: false
  };

  componentDidMount() {
    if (!this.props.categoriesArticle || this.props.categoriesArticle.length === 0) {
      this.props.fetchArticlesStaticCategories();
    } else {
      const { category } = this.props.match.params;
      this.props.fetchArticlesCategory(1, ARTICLE_PER_PAGE, category);
      this.props.fetchGenericSidebar();
      this.updateDimensions();
      window.addEventListener('resize', this.updateDimensions.bind(this));
      this.setState({ currentPage: 1 });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params !== this.props.match.params) {
      this.onPageChanged(1);
      this.setState({ hiddenMenuItems: [] });
    }
    if (
      (!this.props.categoriesArticle ||
        this.props.categoriesArticle.length === 0 ||
        !Array.isArray(this.props.categoriesArticle)) &&
      !this.state.forcecategoriesArticleUpdate
    ) {
      this.props.fetchArticlesStaticCategories();
      this.setState({ hiddenMenuItems: [], forcecategoriesArticleUpdate: true });
    }
    if (this.props.status === STATUS_FAILURE) {
      const { categories } = this.props;
      const categoryObject = categories.find(c => c.slug === this.props.match.params.category);
      if (!categoryObject) {
        this.props.history.push('/404');
      }
    }
    if (prevProps !== this.props || this.state.reloadMenu) {
      this.updateDimensions();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }

  resetMenuElementRef = (c: ?MenuElement) => {
    this._menuElements = [];
    this.addMenuElementRef(c);
  };

  addMenuElementRef = (c: ?MenuElement) => {
    this._menuElements.push(c);
  };

  isDisplayed = (id: number) => {
    return !this.state.hiddenMenuItems.includes(id);
  };

  updateDimensions() {
    const menuLink = this._menuLink;
    if (menuLink && this._menuElements) {
      const menuLinkBounds = menuLink.getBoundingClientRect();
      const menuLinkX = menuLinkBounds.left;

      if (menuLinkX !== this.state.menuLinkX) this.setState({ menuLinkX });
      const maxWidth = menuLink.offsetWidth;

      const width_menu_links = this._menuElements.reduce(
        (total, item) => (item ? total + item.getSize() * 1.1 : total),
        0
      );
      const totalWidth = width_menu_links;

      let currentWidth = totalWidth;
      let deleted = [];
      for (let i = this._menuElements.length - 1; i > 0; i--) {
        if (currentWidth > maxWidth || window.innerWidth <= 767) {
          if (this._menuElements[i]) currentWidth -= this._menuElements[i].getSize();
          deleted = [...deleted, i];
        }
      }
      if (this.state.isMobile) {
        this.setState({ isMobile: false });
      }
      let reload = false;
      if (deleted.join('_') !== this.state.hiddenMenuItems.join('_')) {
        reload = true;
      }
      if (
        (deleted.length !== this.state.hiddenMenuItems.length &&
          (deleted.length > 0 || this.state.hiddenMenuItems.length > 0) &&
          !deleted.every(id => this.state.hiddenMenuItems.includes(id))) ||
        !this.state.hiddenMenuItems.every(id => deleted.includes(id))
      ) {
        this.setState({ hiddenMenuItems: deleted });
      }
      if (reload) {
        this.setState({ reloadMenu: true });
      } else {
        this.setState({ reloadMenu: false });
      }
    }
  }

  renderMenuElements = () => {
    const {
      categoriesArticle,
      match: {
        params: { category }
      }
    } = this.props;
    if (!categoriesArticle || !Array.isArray(categoriesArticle) || categoriesArticle.length === 0) {
      return '';
    }
    return categoriesArticle.map((categorie, index) => (
      <MenuElement
        key={categorie.slug}
        url={`/actualites/${categorie.slug}`}
        urlType="category"
        title={categorie.name}
        isActive={categorie.slug === category}
        isMobile={true}
        ref={index === 0 ? this.resetMenuElementRef : this.addMenuElementRef}
        id={index}
        isDisplayed={this.isDisplayed}
        menuX={this.state.menuLinkX}
      />
    ));
  };

  onPageChanged = (currentPage: number) => {
    const { category } = this.props.match.params;
    this.props.fetchArticlesCategory(currentPage, ARTICLE_PER_PAGE, category);
    this.setState({ currentPage });
  };

  renderArticles = (articles: Array<ArticleType>): any => {
    const { userPref } = this.props;
    return articles.map((article, index) => {
      if (index === 0) {
        return null;
      }
      return (
        <NewsListElement
          key={index}
          url={`/actualites/${article.label.slug}/${article.slug}`}
          img={article.illustration}
          srcset={article.srcset}
          title={article.title}
          date={article.date}
          description={article.caption}
          userPref={userPref}
          tags={article.tags}
          excerpt={article.excerpt !== '' ? article.excerpt : article.meta_description}
          readingTime={article.readingTime}
          hasMetaData
        />
      );
    });
  };

  renderSidebar = () => {
    const { sidebar } = this.props;
    return <SideBar sidebarContent={sidebar.data} status={sidebar.status} />;
  };

  render() {
    if (this.props.status !== STATUS_SUCCESS) {
      return <Loading isBlue backgroundColor={'#013974'} />;
    }

    const hiddenMenuElements = this.state.hiddenMenuItems
      .filter(id => {
        if (!this._menuElements[id]) {
          return false;
        }
        return true;
      })
      .map(id => {
        return this._menuElements[id];
      });
    const mainArticle: ArticleType = this.props.articles.length > 0 ? this.props.articles[0] : nullLoadingArticle;
    const articles: Array<ArticleType> = this.props.articles;
    const { category } = this.props.match.params;

    const categoryObject = this.props.categories.find(c => c.slug === category);
    const categoryName = categoryObject ? categoryObject.name : category;

    return (
      <React.Fragment>
        <section className="banner banner--themes">
          <div
            className="banner__thumbnail img-green"
            style={{ backgroundImage: `url(${getPublicAssets('/img/banner/hub-actualite.jpg')})` }}
          />
          <div className="container">
            <Breadcrumb
              elements={[{ name: 'Accueil', to: '/' }, { name: 'Actualités', to: '/actualites' }, categoryName]}
              isDark={true}
            />
            <h1 className="ft-h2">Actualités {categoryName}</h1>
            <ul className="home-slider__links ft-500" ref={c => (this._menuLink = c)}>
              <li>
                <span>Par thématiques :</span>
              </li>
              <li>
                <Link to={'/actualites'}>Toutes les thématiques</Link>
              </li>
              {this.renderMenuElements()}
              <DropdownMenuElementPlus
                label={this.state.labelPlus}
                noIsHidden={true}
                hiddenMenuElements={hiddenMenuElements}
                isMobile={this.state.isMobile}
                id={6}
              />
            </ul>
          </div>
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9 banner-over banner-over--themes">
                    <Link
                      to={`/actualites/${mainArticle.label.slug}/${mainArticle.slug}`}
                      className="news news--big mb-4 mb-md-2 mb-lg-5"
                    >
                      <article>
                        <BackgroundImage
                          className="news__thumbnail img-blue"
                          illustration={mainArticle.illustration}
                          srcset={mainArticle.srcset}
                          forceState={'medium_large'}
                        />
                        <div className="newsletter-category__main-article">
                          <h3 className="news__title">{mainArticle.title}</h3>
                          <p className="ft-h6">
                            <span className="news__date">{formatFullDate(getDateFromString(mainArticle.date))}</span>
                            {mainArticle.readingTime > 0 && (
                              <span className="news__date">
                                {` - Temps de lecture : ${formatMinutes(new Date(mainArticle.readingTime))}`}
                              </span>
                            )}
                          </p>
                          <p className="news__desc">
                            {mainArticle.excerpt !== '' ? mainArticle.excerpt : mainArticle.meta_description}
                          </p>
                          {mainArticle.tags && !!mainArticle.tags.length && (
                            <p className="mb-1">
                              {mainArticle.tags.map(tag => (
                                <Label key={tag.id} label={tag.name} />
                              ))}
                            </p>
                          )}
                        </div>
                      </article>
                    </Link>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-lg-8on9">
                    {this.renderArticles(articles)}
                    <Pagination
                      current={this.state.currentPage}
                      minimum={1}
                      maximum={Math.ceil(this.props.articleNumber / ARTICLE_PER_PAGE)}
                      isMaximumLastPage={
                        this.state.currentPage >= Math.ceil(this.props.articleNumber / ARTICLE_PER_PAGE)
                      }
                      onPageChanged={this.onPageChanged}
                    />
                  </div>
                </div>
              </section>
            </div>
            {this.renderSidebar()}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withHelmet(NewsletterCategory)((Helmet, pageProps) => {
  const {
    match: { params, url }
  } = pageProps;
  const articleThematics = pageProps.categories.filter(category => category.slug === params.category);
  let articleThematic = '';
  if (articleThematics.length > 0 && articleThematics[0].name) {
    articleThematic = articleThematics[0].name;
  }
  const meta = getMetaBySlug(url, params);
  return (
    <Helmet>
      <title>{meta.title(articleThematic)}</title>
      <meta name={'description'} content={meta.description(articleThematic)} />
      <meta property={'og:title'} content={meta.title(articleThematic)} />
      <meta property={'og:description'} content={meta.description(articleThematic)} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
